import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { TRANSACTIONS_PAGE_SIZE } from "../transactionsManageConstants";

/**
 * Performs the GET request and pushes results through setters.
 * The API returns a plain array (no envelope) for this endpoint.
 * @param {Object} args - Fetch arguments
 * @returns {Promise<void>}
 */
async function fetchPending({ page, signal, setItems, setHasMore, setError, setLoading }) {
    setLoading(true);
    setError(null);
    try {
        const { data } = await axios.get(`/admin/transactions/pending/per_page/${page}`);
        if (signal.aborted) return;
        const items = Array.isArray(data) ? data : [];
        setItems(items);
        setHasMore(items.length >= TRANSACTIONS_PAGE_SIZE);
    } catch (err) {
        if (!signal.aborted) {
            const msg = err?.response?.data;
            setError(typeof msg === "string" ? msg : "Impossible de charger les transactions");
        }
    } finally {
        if (!signal.aborted) setLoading(false);
    }
}

/**
 * Hook returning the pending transactions state for the given page.
 * Refetches on page change and aborts in-flight requests on cleanup.
 * Exposes a `refresh` to re-trigger the current page (e.g. after approve).
 * @param {number} page - 1-indexed page number
 * @returns {{ items: Array, hasMore: boolean, loading: boolean, error: string|null, refresh: Function }}
 */
function usePendingTransactions(page) {
    const [items, setItems] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [tick, setTick] = useState(0);

    useEffect(() => {
        const controller = new AbortController();
        fetchPending({ page, signal: controller.signal, setItems, setHasMore, setError, setLoading });
        return () => controller.abort();
    }, [page, tick]);

    const refresh = useCallback(() => setTick((t) => t + 1), []);
    return { items, hasMore, loading, error, refresh };
}

export default usePendingTransactions;
